/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Center, Container, Flex, Tab, TabList, TabPanel, TabPanels, Tabs, Text, Tooltip } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import BreadcrumbComponent from '../../../../components/BreadcrumbComponent/BreadcrumbComponent';
import Company from '../../../../components/Company.js';
import AnnuallyExcelSummarizedReportSFP from '../../../../components/EarlyWarning/SummarizedReport/AnnuallyExcelSummarizedReportSFP.js';
import AnnuallyExcelSummarizedReportSPLOCI from '../../../../components/EarlyWarning/SummarizedReport/AnnuallyExcelSummarizedReportSPLOCI.js';
import NoData from '../../../../components/NoData.js';
import PrintLayout from '../../../../components/Print/PrintLayout';
import c from '../../../../constant';
import useStore from '../../../../store/index.js';
import { getDefaultFilterOptionValue, getFilterOptions } from '../../../../utils/ews.js';
import { mappingKhmerNumber } from '../../../../utils/market-development.js';
import { getReportTitle } from '../../../MarketDevelopment/Utils/index.js';
import { getFilterTitle, getYear } from '../../utils.js';
import Filter from '../ews-annually-summary-filter.js';
import SFPSpreadSheetTable from './SFPSpreadSheetTable.js';
import SPLOCISpreadSheetTable from './SPLOCISpreadSheetTable';

function SummarizedFinancialStatementReport({ industrial = false }) {
	const location = useLocation();
	const history = useHistory();
	const urlParams = new URLSearchParams(location.search);
	const period = urlParams.get('period') ?? null;
	const reportId = urlParams.get('reportId') ?? null;
	const report = urlParams.get('report') ?? null;
	const years = urlParams.get('years') ?? null;
	const companyType = urlParams.get('companyType') ?? null;
	const companyId = urlParams.get('companyId') ?? null;
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state?.currentLang);
	const [tabIndex, setTabIndex] = useState(0);
	const [exportSFP, setExportSFP] = useState(null);
	const [exportSPLOCI, setExportSPLOCI] = useState(null);

	const reportMapping = useMemo(
		() => ({
			annually_audited_financial_statements: 'AnnualAudited',
			annually_unaudited_financial_statements: 'AnnualNonAudited',
		}),
		[],
	);

	const reportTitleMapping = useMemo(
		() => ({
			annually_audited_financial_statements: 'Annually Audited Financial Summarized Report',
			annually_unaudited_financial_statements: 'Annually Unaudited Financial Summarized Report',
		}),
		[],
	);

	const { currentUser: user } = useStore((state) => state?.currentUser);
	const [filterOptions, setFilterOptions] = useState([]);
	const [isPermitted, setPermitted] = useState(false);
	const [title, setTitle] = useState(null);
	useEffect(() => setFilterOptions(getFilterOptions({ industrial, user })), [user]);
	useEffect(() => setPermitted(filterOptions?.length > 0), [filterOptions]);
	useEffect(() => setTitle(companyType ? companyType : getDefaultFilterOptionValue({ filterOptions })), [companyType, filterOptions]);

	return (
		<PrintLayout>
			<>
				<Container maxW="container.xl" mt="16px" mb="16px">
					<BreadcrumbComponent
						list={[
							{
								name: 'Insurance Institute Report',
								path: '/company-submission-report',
							},
							{
								name: period,
								path: '/company-submission-report?period=' + period,
							},
							{
								name: getReportTitle(reportId),
								path: `/early-warning-system?period=${period}&reportId=${reportId}&page=1&report=${report}`,
							},
							{
								name: reportTitleMapping[reportId],
								path: '#',
							},
						]}
					/>
					<Center mt="5">
						<Box
							w="100%"
							bg="white"
							borderRadius={c.borderRadius}
							border="1px solid #dbdbdb"
							boxShadow="0 1px 2px rgba(var(--black, #000), 0.1)"
							pb="32px"
						>
							<Flex
								className="hidden-print"
								bg="#FAFAFA"
								p="16px"
								pt="8px"
								pb="8px"
								mb="0px"
								alignItems="center"
								justifyContent="space-between"
								borderBottom="1px solid #dbdbdb"
							>
								<Text fontSize="x-large" fontWeight="bold">
									{t(reportTitleMapping[reportId])}
								</Text>
								<Flex>
									{tabIndex === 0 && isPermitted && <AnnuallyExcelSummarizedReportSFP object={exportSFP} mr={2} />}
									{tabIndex === 1 && isPermitted && <AnnuallyExcelSummarizedReportSPLOCI object={exportSPLOCI} mr={2} />}
									{isPermitted && (
										<Filter
											industrial={industrial}
											reportId={reportId}
											report={report}
											years={years}
											companyId={companyId}
											companyType={companyType}
											onCallback={(url) => history.push(`/early-warning-system/annually-summarized-fs-report${url}`)}
										/>
									)}
								</Flex>
							</Flex>
							{isPermitted ? (
								<>
									<Flex flexDirection="column" justifyContent="center" alignItems="center" pt={4} pl={4} pr={4} pb={1}>
										<Text fontSize="xl" fontWeight="700" color="blue.500" mb={1}>
											{t(getFilterTitle({ title, industrial }))}
										</Text>
										{companyId && (
											<Box mb={1}>
												<Company id={companyId} color="blue.500" />
											</Box>
										)}
										<Text fontSize="xl" fontWeight="700" color="blue.500">
											{(function () {
												if (years) {
													const parsed = JSON.parse(years)
														.map((year) => parseInt(year))
														.sort((a, b) => (a > b ? 1 : -1));
													if (parsed?.length > 0) {
														const ys = parsed?.map((p) => (lang === 'kh' ? mappingKhmerNumber(getYear(p)) : `${getYear(p)}`));
														return ys.join(', ');
													}
												}
												return lang === 'kh' ? mappingKhmerNumber(getYear(null)) : `${getYear(null)}`;
											})()}
										</Text>
									</Flex>
									<Box pt={0} pl={4} pr={4}>
										<Tabs index={tabIndex} onChange={(index) => setTabIndex(index)}>
											<TabList borderBottomWidth="1px" borderBottomStyle="solid" borderBottomColor="gray.200">
												<Tab>
													<Tooltip label={t('SFP')}>
														<Text>{t('sfp')}</Text>
													</Tooltip>
												</Tab>
												<Tab>
													<Tooltip label={t('SPLOCI')}>
														<Text>{t('sploci')}</Text>
													</Tooltip>
												</Tab>
											</TabList>
											<TabPanels>
												<TabPanel pl={0} pr={0} pb={0}>
													<SFPSpreadSheetTable
														filterOptions={filterOptions}
														years={years}
														type={reportMapping[reportId]}
														companyType={companyType}
														companyId={industrial ? null : companyId}
														onReadyForExport={(result) => setExportSFP(result)}
													/>
												</TabPanel>
												<TabPanel pl={0} pr={0} pb={0}>
													<SPLOCISpreadSheetTable
														filterOptions={filterOptions}
														years={years}
														type={reportMapping[reportId]}
														companyType={companyType}
														companyId={industrial ? null : companyId}
														onReadyForExport={(result) => setExportSPLOCI(result)}
													/>
												</TabPanel>
											</TabPanels>
										</Tabs>
									</Box>
								</>
							) : (
								<>
									<NoData />
								</>
							)}
						</Box>
					</Center>
				</Container>
			</>
		</PrintLayout>
	);
}

export default SummarizedFinancialStatementReport;
